import React, { useState, useRef, useEffect } from 'react';
import {
  StatusTip,
  SearchBox,
  SelectOptionWithGroup,
  Select,
  ChangeContext
} from 'tea-component';
import GameContent from './GameContent';
import { RecordItemType } from '../type';
import { useIntl, getLocale } from 'umi';
import classNames from 'classnames';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';

const isCurrent = (data: any, searchKeyword: string): boolean =>
  data?.name === searchKeyword || data?.name_cn === searchKeyword;

export default function SearchRecent(props: any) {
  const {
    initSearchInput,
    searchDisplayValue,
    loading,
    placeholder,
    handleChangeInput = () => console.log,
    handleClear = () => console.log,
    clickSearchItem: clickSearchItemProp = () => console.log,
    deleteRecordItem: deleteRecordItemProp,
    // searchList,
    recentRecordList = [],
    searchResultList,
    isCustomRendering,
    renderComp,
    disabled,
    // nameStr 根据中英文传递不同的取值字段时，为了防止没有中文、或者英文时，就通过这个字段默认取英文
    defaultNameStr = 'lineOneStr',
    nameStr = 'lineOneStr',
    // 有选择框的情况下，联动进行清空
    resetSearchType,
    searchType = 'mobile',
    defaultImg,
    // 仅有一个搜索结果时，是否自动触发选中行为
    isAutoSelectWhenOnlyOneSearchResult = false,
    // 更新AutoComplete组件options的 flag，在true、false来回切换即可 触发更新
    updateFlagOfAutoCompleteOptions,
    // 搜索关键字为空时，是否触发 handleChangeInput事件
    triggerInputChangeEventWhenEmptyInput = false,
    fetchParams,
    // renderType: renderTypeProps = 'record',
    recordNodataList = [],
  } = props;
  // console.debug('SearchRecent_props:', props);
  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;
  const timer: any = useRef(null);
  const [inputValue, setInputValue] = useState<any>('');
  const [isDropDownOpen, setDropdownOpen] = useState<any>(false);
  const [selectionOpen, setSelectionOpen] = useState<boolean>(false);
  const [renderType, setRenderType] = useState<'record' | 'search' | 'nodata'>('record');
  const [options, setOptions] = useState<SelectOptionWithGroup[]>([]);
  const stopPropagationRef = useRef(false);
  const groups = {
    recentSearch: formatMessage({ id: 'dbi_top_recent' }),
  };

  const clickSearchItem = (cvm: RecordItemType) => {
    if (stopPropagationRef.current) {
      stopPropagationRef.current = false;
      return;
    }
    const inputValueTmp = cvm?.[nameStr] || cvm?.[defaultNameStr];
    setInputValue(inputValueTmp);
    clickSearchItemProp(renderType, cvm);
  };

  const deleteRecordItem = (cvm: RecordItemType) => {
    stopPropagationRef.current = true;
    if (deleteRecordItemProp instanceof Function) {
      deleteRecordItemProp(cvm);
    }
  };

  useEffect(() => {
    setInputValue('');
    setRenderType('record');
  }, [resetSearchType]);

  useEffect(() => {
    let searchList = [];
    let realRenderType = renderType;
    if (realRenderType !== 'search') {
      if (Array.isArray(recentRecordList) && recentRecordList?.length > 0) {
        searchList = recentRecordList;
        realRenderType = 'record';
      } else {
        searchList = recordNodataList;
        realRenderType = 'nodata';
      }
    } else {
      searchList = searchResultList;
    }
    setRenderType(realRenderType);

    if (Array.isArray(searchList)) {
      const opts = searchList.map((item: RecordItemType) => ({
        groupKey: realRenderType === 'search' || realRenderType === 'nodata' ? undefined : 'recentSearch',
        value: item?.id,
        text: isCustomRendering ? (renderComp(item, {
          triggerCompClick: clickSearchItem,
          onClearIcon: deleteRecordItem,
          showClearIcon: realRenderType === 'record',
        })) : (<GameContent
          cvm={item}
          showClearIcon={realRenderType === 'record'}
          onClearIcon={deleteRecordItem}
          onClick={clickSearchItem}
          entityType={searchType}
          defaultImg={defaultImg}
        />)
      }));
      setOptions(opts);
    }
  }, [
    locale,
    isCustomRendering,
    renderComp,
    JSON.stringify(recordNodataList),
    JSON.stringify(searchResultList),
    JSON.stringify(recentRecordList),
    JSON.stringify(fetchParams),
    renderType,
    updateFlagOfAutoCompleteOptions,
  ]);

  // isAutoSelectWhenOnlyOneSearchResult开启，并且仅有一个搜索结果时，直接触发选中行为，并且关闭AutoComplete的下拉浮窗
  useEffect(() => {
    if (!isAutoSelectWhenOnlyOneSearchResult) return;
    if (renderType !== 'search') return;
    if (searchResultList?.length !== 1 || !searchResultList?.[0]) return;
    clickSearchItem(searchResultList?.[0]);
    // 模拟click事件，目的是为了 仅有一个搜索结果时，直接选中的时候 可以关闭AutoComplete的下拉浮窗
    setTimeout(() => {
      const node = document.querySelector('.dbi-recent-complete > .tea-dropdown-box > div:first-of-type .tea-list > li:first-of-type');
      try {
        (node as any)?.click();
      } catch (err) {
        console.warn(err);
      }
    });
  }, [isAutoSelectWhenOnlyOneSearchResult, renderType, JSON.stringify(searchResultList), JSON.stringify(fetchParams)]);

  useEffect(() => {
    if (initSearchInput) {
      setInputValue('');
      // 重置搜索状态时，清空搜索关键字时，同时也重置为 展示搜索记录
      setRenderType('record');
    }
  }, [initSearchInput]);

  useEffect(() => {
    if (searchDisplayValue) {
      setInputValue(searchDisplayValue);
    }
  }, [searchDisplayValue]);

  const onSearch = () => {
    onPressEnter();
  };

  const onPressEnter = () => {
    // console.log('search');
    if (!inputValue) {
      // 输入内容不是空，则是search，否则展示record
      setRenderType('record');
    } else {
      setRenderType('search');
    }
  };

  const onChange = (value: string, context: ChangeContext) => {
    isDropDownOpen && context?.event?.stopPropagation()
    setInputValue(value);
    if (typeof value !== 'string') {
      return;
    }
    if (timer?.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    // 去除空格，判断是不是全都是空格
    const _val = value.replace(/^\s*/g, '');
    if (!_val) {
      // 为空时不请求,并展示为record
      onClear();
      if (triggerInputChangeEventWhenEmptyInput) {
        clearTimeout(timer.current);
        timer.current = null;
        handleChangeInput(_val);
      }
      return;
    }
    /** 防抖 + 为空时展示record */
    timer.current = setTimeout(() => {
      handleChangeInput(_val);
      setRenderType('search');
    }, 500);
  };

  const onClear = () => {
    setInputValue('');
    handleClear();
    setRenderType('record');
  };

  useEffect(() => {
    if (!inputValue?.trim()) return;
    const currentObj = recentRecordList?.find((item: any) => isCurrent(item, inputValue))
      || searchResultList?.find((item: any) => isCurrent(item, inputValue));
    const inputValueTmp = isCN ? (currentObj?.name_cn || currentObj?.name) : currentObj?.name;
    if (inputValueTmp) {
      setInputValue(inputValueTmp);
    }
  }, [isCN]);

  const determineTip = () => {
    if (loading && renderType !== 'record') {
      return (
        <StatusTip
          status={'loading'}
          loadingText={formatMessage({ id: 'dbi_search_loading' })}
        />
      );
    }

    if (!loading && renderType === 'search' && !searchResultList?.length && !options?.length) {
      return (
        <StatusTip
          style={{ height: '80px', display: 'flex', justifyContent: 'center' }}
          status={'empty'}
          emptyText={
            <span className='dbi-coustom-search-input-empty-text'>{formatMessage({ id: 'dbi_search_no_result' })}</span>
          }
        />
      );
    }
    if (renderType === 'nodata') {
      return (
        <StatusTip
          status={'loading'}
          loadingText={formatMessage({ id: 'dbi_search_loading' })}
        />
      );
    }

  }



  return (
    <>
      <Select
        overlayClassName={classNames('dbi-recent-complete', {
          'dbi-recent-complete-unsethight': renderType === 'record', // recent不限制高度
          'dbi-recent-complete-hight': renderType === 'search' && options?.length, // 考虑无数据情况
          'dbi-recent-complete-hight-no-recent-data': renderType === 'search' && !searchResultList?.length && !loading,
        })}
        appearance="pure"
        options={options}
        open={renderType === 'search' ? !!inputValue && selectionOpen : recentRecordList.length && selectionOpen}
        value={inputValue}
        groups={renderType === 'search' ? undefined : groups}
        closeOnScroll={true}
        listWidth={300}
        listHeight={320}
        tips={
          determineTip()
        }
        onOpen={() => setDropdownOpen(true)}
        onOpenChange={(v) => {
          setSelectionOpen(v)
          !v && setDropdownOpen(false)
        }}
        onFocus={() => setSelectionOpen(true)}
        onBlur={() => setSelectionOpen(false)}
        button={
          <SearchBox
            className="dbi-recent-select"
            disabled={disabled}
            placeholder={
              placeholder ??
              formatMessage({ id: 'dbi_default_search_placeholder' })
            }
            value={inputValue}
            onSearch={onSearch}
            onKeyDown={(e) => {
              e?.key === ' ' && e?.stopPropagation()
              e?.key === 'Enter' && e?.stopPropagation()
            }}
            onPressEnter={onPressEnter}
            onChange={onChange}
            onClick={(e) => {
              selectionOpen && e?.stopPropagation()
            }}
          />
        }
      />
    </>

  );
}
